import {
  clearValidationForAllParameters, clearValidationForKeys, clearValidationForSingleParameter
} from 'app/area/shared/report/form/validation';
import { Reducer, Action } from 'redux';
import {
  ACTION, ReceiveLanguageOptions,
  RequestFavourite, ReceiveFavourite, FailFavourite,
  RequestDeleteFavourite, ReceiveDeleteFavourite, FailDeleteFavourite,
  RequestSaveFavourite, ReceiveSaveFavourite, FailSaveFavourite,
  UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation, ResetValidation, RequestDeleteSchedule, ReceiveDeleteSchedule, FailDeleteSchedule,
  ChangeLongDescription, CopyLongDescriptionToAll
} from './action-types';
import { languageValidationParameter, longDescriptionValidationParameter } from './constants';
import { FavouriteAdminFormState, FavouriteItem } from './types-store';
import { STATE } from 'app/constants';
import { withOptionsReducer } from 'app/components/options-handler';

export const INITIAL_STATE: FavouriteAdminFormState = {
  favouriteItem: undefined,
  originalFavouriteItem: undefined,
  newFavouriteId: undefined,
  fetching: false,
  scheduleDeleting: false,
  deleting: false,
  saving: false,
  languageOptions: [],
  validation: {}
};

const reducers: { [indexer: string]: Reducer<FavouriteAdminFormState, Action> } = {

  [ACTION.RECEIVE_LANGUAGE_OPTIONS]: (state: FavouriteAdminFormState,
    action: ReceiveLanguageOptions): FavouriteAdminFormState => {

    return {
      ...state,
      languageOptions: action.options
    };
  },

  [ACTION.REQUEST_FAVOURITE]: (state: FavouriteAdminFormState,
    action: RequestFavourite): FavouriteAdminFormState => {

    return {
      ...state,
      favouriteItem: undefined,
      originalFavouriteItem: undefined,
      newFavouriteId: undefined,
      fetching: true,
      saving: false,
      validation: {}
    };
  },

  [ACTION.RECEIVE_FAVOURITE]: (state: FavouriteAdminFormState,
    action: ReceiveFavourite): FavouriteAdminFormState => {

    return {
      ...state,
      favouriteItem: action.item,
      originalFavouriteItem: action.item,
      fetching: false
    };
  },

  [ACTION.FAIL_FAVOURITE]: (state: FavouriteAdminFormState,
    action: FailFavourite): FavouriteAdminFormState => {

    return {
      ...state,
      fetching: false
    };
  },

  [ACTION.REQUEST_DELETE_FAVOURITE]: (state: FavouriteAdminFormState,
    action: RequestDeleteFavourite): FavouriteAdminFormState => {

    return {
      ...state,
      deleting: true
    };
  },

  [ACTION.RECEIVE_DELETE_FAVOURITE]: (state: FavouriteAdminFormState,
    action: ReceiveDeleteFavourite): FavouriteAdminFormState => {

    return {
      ...state,
      deleting: false
    };
  },

  [ACTION.FAIL_DELETE_FAVOURITE]: (state: FavouriteAdminFormState,
    action: FailDeleteFavourite): FavouriteAdminFormState => {

    return {
      ...state,
      deleting: false
    };
  },

  [ACTION.REQUEST_SAVE_FAVOURITE]: (state: FavouriteAdminFormState,
    action: RequestSaveFavourite): FavouriteAdminFormState => {

    return {
      ...state,
      saving: true
    };
  },

  [ACTION.RECEIVE_SAVE_FAVOURITE]: (state: FavouriteAdminFormState,
    action: ReceiveSaveFavourite): FavouriteAdminFormState => {

    return {
      ...state,
      newFavouriteId: action.newFavouriteId,
      originalFavouriteItem: state.favouriteItem,
      saving: false
    };
  },

  [ACTION.FAIL_SAVE_FAVOURITE]: (state: FavouriteAdminFormState,
    action: FailSaveFavourite): FavouriteAdminFormState => {

    return {
      ...state,
      saving: false
    };
  },

  [ACTION.CHANGE_LANGUAGE]: (state: FavouriteAdminFormState,
    action: ChangeLanguage): FavouriteAdminFormState => {

    const favouriteItem = state.favouriteItem || {} as FavouriteItem;
    const language = favouriteItem.language || [];

    return {
      ...state,
      favouriteItem: {
        ...favouriteItem,
        language: language.some(languageItem => languageItem.value === action.languageId)
          ? language.map(
            languageItem => languageItem.value === action.languageId
              ? {
                ...languageItem,
                description: action.description
              }
              : languageItem
          )
          : [
            ...language,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(state.validation, 'language', languageValidationParameter, action.languageId)
    };
  },

  [ACTION.COPY_LANGUAGE_TO_ALL]: (state: FavouriteAdminFormState,
    action: CopyLanguageToAll): FavouriteAdminFormState => {

    const favouriteItem = state.favouriteItem || {} as FavouriteItem;
    const language = favouriteItem.language || [];
    const targetLanguage = language.find(languageItem => languageItem.value === action.languageId);

    if (targetLanguage === undefined) {
      return state;
    }

    const updatedLanguage = language.map(
      languageItem => ({
        ...languageItem,
        description: targetLanguage.description
      })
    );
    const newLanguage = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !language.some(languageItem => languageItem.value === languageId))
      .map(languageId => ({
        value: languageId,
        description: targetLanguage.description
      }));

    return {
      ...state,
      favouriteItem: {
        ...favouriteItem,
        language: [
          ...updatedLanguage,
          ...newLanguage
        ]
      },
      validation: clearValidationForAllParameters(state.validation, 'language', languageValidationParameter)
    };
  },

  [ACTION.CHANGE_LONG_DESCRIPTION]: (state: FavouriteAdminFormState,
    action: ChangeLongDescription): FavouriteAdminFormState => {

    const favouriteItem = state.favouriteItem || {} as FavouriteItem;
    const longDescription = favouriteItem.longDescription || [];

    return {
      ...state,
      favouriteItem: {
        ...favouriteItem,
        longDescription: longDescription.some(longDescriptionItem => longDescriptionItem.value === action.languageId)
          ? longDescription.map(
            longDescriptionItem => longDescriptionItem.value === action.languageId
              ? {
                ...longDescriptionItem,
                description: action.description
              }
              : longDescriptionItem
          )
          : [
            ...longDescription,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(
        state.validation,
        'longDescription',
        longDescriptionValidationParameter,
        action.languageId
      )
    };
  },

  [ACTION.COPY_LONG_DESCRIPTION_TO_ALL]: (state: FavouriteAdminFormState,
    action: CopyLongDescriptionToAll): FavouriteAdminFormState => {

    const favouriteItem = state.favouriteItem || {} as FavouriteItem;
    const longDescription = favouriteItem.longDescription || [];
    const targetLongDescription = longDescription.find(longDescriptionItem => longDescriptionItem.value === action.languageId);

    if (targetLongDescription === undefined) {
      return state;
    }

    const updatedlongDescription = longDescription.map(
      languageItem => ({
        ...languageItem,
        description: targetLongDescription.description
      })
    );
    const newlongDescription = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !longDescription.some(languageItem => languageItem.value === languageId))
      .map(languageId => ({
        value: languageId,
        description: targetLongDescription.description
      }));

    return {
      ...state,
      favouriteItem: {
        ...favouriteItem,
        longDescription: [
          ...updatedlongDescription,
          ...newlongDescription
        ]
      },
      validation: clearValidationForAllParameters(state.validation, 'longDescription', longDescriptionValidationParameter)
    };
  },

  [ACTION.UPDATE_ITEM]: (state: FavouriteAdminFormState,
    action: UpdateItem): FavouriteAdminFormState => {

    const favouriteItem = {
      ...(state.favouriteItem || {}),
      ...action.item
    } as FavouriteItem;
    const validation = clearValidationForKeys(state.validation, Object.keys(action.item) as any[]);

    return {
      ...state,
      favouriteItem: favouriteItem,
      validation
    };
  },

  [ACTION.REQUEST_DELETE_SCHEDULE]: (state: FavouriteAdminFormState,
    action: RequestDeleteSchedule): FavouriteAdminFormState => {

    return {
      ...state,
      scheduleDeleting: true
    };
  },

  [ACTION.RECEIVE_DELETE_SCHEDULE]: (state: FavouriteAdminFormState,
    action: ReceiveDeleteSchedule): FavouriteAdminFormState => {

    return {
      ...state,
      scheduleDeleting: false
    };
  },

  [ACTION.FAIL_DELETE_SCHEDULE]: (state: FavouriteAdminFormState,
    action: FailDeleteSchedule): FavouriteAdminFormState => {

    return {
      ...state,
      scheduleDeleting: false
    };
  },

  [ACTION.CLEAR_FORM]: (state: FavouriteAdminFormState,
    action: ClearForm): FavouriteAdminFormState => {

    return {
      ...state,
      ...INITIAL_STATE,
      languageOptions: state.languageOptions
    };
  },

  [ACTION.UPDATE_VALIDATION]: (state: FavouriteAdminFormState,
    action: UpdateValidation): FavouriteAdminFormState => {
    return {
      ...state,
      validation: { ...state.validation, ...action.validation }
    };
  },

  [ACTION.RESET_VALIDATION]: (state: FavouriteAdminFormState, action: ResetValidation): FavouriteAdminFormState => {
    return {
      ...state,
      validation: { ...state.validation, [action.key]: { messages: [] } }
    };
  }
};

const stateReducer: Reducer<FavouriteAdminFormState> = (
  state: FavouriteAdminFormState = INITIAL_STATE, action: Action) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};

export const reducer = withOptionsReducer(STATE.administrationEdit, stateReducer);
